import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Event } from '../types/events';
import type { SingleResponse } from '../types/api';

type EventResponse = Event;
type DefaultError = unknown;

type UseEventParams = {
  eventId?: string;
  include?: string;
  enabled?: boolean;
};

export function useEvent<TData = EventResponse, TError = DefaultError>(
  { eventId, include, enabled = true }: UseEventParams = {},
  options?: Omit<
    UseQueryOptions<EventResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['event', eventId, include];

  return useQuery<EventResponse, TError, TData>({
    queryKey,
    enabled: enabled && !!eventId,
    queryFn: async () => {
      if (!eventId) {
        throw new Error('eventId is required to fetch an event');
      }

      const response = (await service.getEvent(eventId, {
        ...(include ? { include } : {}),
      })) as SingleResponse<Event>;

      return response.data;
    },
    ...(options as UseQueryOptions<EventResponse, TError, TData, QueryKey>),
  });
}
